import { useRef, useState, useEffect, useCallback } from "react";
import { sanitizeInterviewHtml } from "../../lib/sanitizeWordHtml";
import { uploadImageFile, classifyUploadError } from "../../lib/uploadImage";

function escapeHtml(text: string): string {
	return text
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;")
		.replace(/"/g, "&quot;")
		.replace(/'/g, "&#039;");
}

interface RichTextEditorProps {
	value: string;
	onChange: (html: string) => void;
	placeholder?: string;
	minHeight?: string;
	label?: string;
	required?: boolean;
	allowHtmlPaste?: boolean;
}

export default function RichTextEditor({
	value,
	onChange,
	placeholder = "Escribe aqui...",
	minHeight = "280px",
	label,
	required,
	allowHtmlPaste = false,
}: RichTextEditorProps) {
	const editorRef = useRef<HTMLDivElement>(null);
	const toolbarRef = useRef<HTMLDivElement>(null);
	const popoverRef = useRef<HTMLDivElement>(null);
	const imageInputRef = useRef<HTMLInputElement>(null);
	const savedRangeRef = useRef<Range | null>(null);
	const isInteractingRef = useRef(false);
	const [focused, setFocused] = useState(false);
	const [showFloating, setShowFloating] = useState(false);
	const [floatingPos, setFloatingPos] = useState({ top: 0, left: 0 });
	const [activeFormats, setActiveFormats] = useState<Set<string>>(new Set());
	const [linkUrl, setLinkUrl] = useState("");
	const [showLinkPopover, setShowLinkPopover] = useState(false);
	const [uploadingImage, setUploadingImage] = useState(false);
	const [imageError, setImageError] = useState("");
	const [showImagePopover, setShowImagePopover] = useState(false);
	const [imageUrl, setImageUrl] = useState("");
	const [imageCaption, setImageCaption] = useState("");

	// Sync external value
	useEffect(() => {
		const el = editorRef.current;
		if (!el) return;
		if (el.innerHTML !== value) {
			el.innerHTML = value || "";
		}
	}, [value]);

	// Click outside to close popover
	useEffect(() => {
		const handler = (e: MouseEvent) => {
			const target = e.target as Node;
			if (
				showLinkPopover &&
				popoverRef.current &&
				!popoverRef.current.contains(target) &&
				toolbarRef.current &&
				!toolbarRef.current.contains(target)
			) {
				setShowLinkPopover(false);
				setLinkUrl("");
			}
		};
		document.addEventListener("mousedown", handler);
		return () => document.removeEventListener("mousedown", handler);
	}, [showLinkPopover]);

	const emitChange = useCallback(() => {
		const html = editorRef.current?.innerHTML || "";
		onChange(html);
	}, [onChange]);

	const saveSelection = useCallback(() => {
		const sel = window.getSelection();
		if (sel && sel.rangeCount > 0 && editorRef.current) {
			const range = sel.getRangeAt(0);
			// Only save if inside editor
			let node = range.commonAncestorContainer;
			if (node.nodeType === Node.TEXT_NODE) node = node.parentNode as Node;
			let inside = false;
			let el = node as HTMLElement | null;
			while (el) {
				if (el === editorRef.current) {
					inside = true;
					break;
				}
				el = el.parentElement;
			}
			if (inside) {
				savedRangeRef.current = range.cloneRange();
			}
		}
	}, []);

	const restoreSelection = useCallback(() => {
		if (!savedRangeRef.current) return;
		const sel = window.getSelection();
		if (!sel) return;
		sel.removeAllRanges();
		sel.addRange(savedRangeRef.current);
	}, []);

	const exec = useCallback((command: string, valueArg: string = "") => {
		restoreSelection();
		document.execCommand(command, false, valueArg);
		editorRef.current?.focus();
		checkActiveFormats();
		emitChange();
	}, [emitChange]);

	const checkActiveFormats = useCallback(() => {
		const formats = new Set<string>();
		if (document.queryCommandState("bold")) formats.add("bold");
		if (document.queryCommandState("italic")) formats.add("italic");
		if (document.queryCommandState("underline")) formats.add("underline");
		if (document.queryCommandState("strikeThrough")) formats.add("strike");
		if (document.queryCommandState("insertUnorderedList")) formats.add("ul");
		if (document.queryCommandState("insertOrderedList")) formats.add("ol");
		const sel = window.getSelection();
		if (sel && sel.rangeCount > 0) {
			let node = sel.getRangeAt(0).commonAncestorContainer as Node | null;
			if (node && node.nodeType === Node.TEXT_NODE) node = node.parentElement;
			while (node && node !== editorRef.current) {
				const tag = (node as HTMLElement).tagName?.toLowerCase();
				if (tag === "h2") formats.add("h2");
				if (tag === "h3") formats.add("h3");
				if (tag === "blockquote") formats.add("quote");
				if (tag === "a") formats.add("link");
				node = (node as HTMLElement).parentNode;
			}
		}
		setActiveFormats(formats);
	}, []);

	// Get URL of current link selection, if any
	const getCurrentLinkUrl = useCallback(() => {
		const sel = window.getSelection();
		if (!sel || sel.rangeCount === 0) return "";
		let node = sel.getRangeAt(0).commonAncestorContainer as Node | null;
		if (node && node.nodeType === Node.TEXT_NODE) node = node.parentElement;
		while (node && node !== editorRef.current) {
			if ((node as HTMLElement).tagName?.toLowerCase() === "a") {
				return (node as HTMLAnchorElement).getAttribute("href") || "";
			}
			node = (node as HTMLElement).parentNode;
		}
		return "";
	}, []);

	const calcFloatingPos = useCallback((range: Range) => {
		if (!editorRef.current) return { top: 0, left: 0 };
		const rect = range.getBoundingClientRect();
		const editorRect = editorRef.current.getBoundingClientRect();
		const toolbarWidth = 320;
		let left = rect.left + rect.width / 2 - toolbarWidth / 2 - editorRect.left;
		let top = rect.top - editorRect.top - 48;
		left = Math.max(8, Math.min(left, editorRect.width - toolbarWidth - 8));
		return { top, left };
	}, []);

	// Show/hide floating toolbar based on selection
	const handleSelectionChange = useCallback(() => {
		// Skip if user is interacting with toolbar/popover
		if (isInteractingRef.current) return;

		const sel = window.getSelection();
		if (!sel) {
			setShowFloating(false);
			return;
		}

		if (sel.isCollapsed || !editorRef.current) {
			setShowFloating(false);
			return;
		}

		// Check selection is inside editor
		let node = sel.anchorNode;
		let inside = false;
		while (node) {
			if (node === editorRef.current) {
				inside = true;
				break;
			}
			node = node.parentNode;
		}
		if (!inside) {
			setShowFloating(false);
			return;
		}

		const range = sel.getRangeAt(0);
		const rect = range.getBoundingClientRect();
		if (rect.width === 0) {
			setShowFloating(false);
			return;
		}

		const pos = calcFloatingPos(range);
		setFloatingPos(pos);
		setShowFloating(true);
		checkActiveFormats();
	}, [calcFloatingPos, checkActiveFormats]);

	useEffect(() => {
		document.addEventListener("selectionchange", handleSelectionChange);
		return () => document.removeEventListener("selectionchange", handleSelectionChange);
	}, [handleSelectionChange]);

	// Also on mouseup/touchend to catch selection end
	const handleMouseUp = () => {
		setTimeout(handleSelectionChange, 10);
	};

	const handleInput = () => {
		emitChange();
		checkActiveFormats();
	};

	const handlePaste = useCallback(
		(e: React.ClipboardEvent) => {
			e.preventDefault();
			const clipboardData = e.clipboardData || (window as any).clipboardData;
			if (!clipboardData) return;

			if (allowHtmlPaste) {
				// Try HTML first, fall back to plain text
				const html = clipboardData.getData("text/html");
				if (html && html.trim()) {
					const cleaned = sanitizeInterviewHtml(html);
					if (cleaned) {
						document.execCommand("insertHTML", false, cleaned);
						emitChange();
						checkActiveFormats();
						return;
					}
				}
			}

			// ALWAYS use plain text — strip all formatting from Word, browsers, etc.
			const plainText = clipboardData.getData("text/plain") || "";

			// Clean invisible control characters / BOM
			let cleaned = plainText
				.replace(/\ufeff/g, "")
				.replace(/\u200b/g, "")
				.replace(/\r\n/g, "\n")
				.replace(/\r/g, "\n");

			// Collapse 3+ consecutive newlines to double newline
			cleaned = cleaned.replace(/\n{3,}/g, "\n\n");

			// Split paragraphs by double newline, fall back to single newline
			const paragraphs = cleaned.includes("\n\n")
				? cleaned.split("\n\n")
				: cleaned.split("\n");

			const contentToInsert = paragraphs
				.filter((p: string) => p.trim())
				.map((p: string) => `<p>${escapeHtml(p.trim())}</p>`)
				.join("");

			if (!contentToInsert) return;

			// Insert at current cursor position
			const selection = window.getSelection();
			if (selection && selection.rangeCount > 0 && editorRef.current) {
				const range = selection.getRangeAt(0);
				// Only insert if inside editor
				let node: Node | null = range.commonAncestorContainer;
				if (node.nodeType === Node.TEXT_NODE) node = node.parentNode;
				let inside = false;
				let el: Node | null = node;
				while (el) {
					if (el === editorRef.current) {
						inside = true;
						break;
					}
					el = (el as HTMLElement).parentNode;
				}

				if (inside) {
					document.execCommand("insertHTML", false, contentToInsert);
					emitChange();
					checkActiveFormats();
				}
			}
		},
		[emitChange, checkActiveFormats, allowHtmlPaste],
	);

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if ((e.metaKey || e.ctrlKey) && e.key === "k") {
			e.preventDefault();
			openLinkPopover();
		}
	};

	const openLinkPopover = useCallback(() => {
		// Set interaction flag IMMEDIATELY (not via useEffect)
		isInteractingRef.current = true;
		saveSelection();

		const existingUrl = getCurrentLinkUrl();
		setLinkUrl(existingUrl);
		setShowLinkPopover(true);

		// Position popover based on saved range
		if (savedRangeRef.current && editorRef.current) {
			const pos = calcFloatingPos(savedRangeRef.current);
			setFloatingPos(pos);
		}

		// Focus input after popover renders
		requestAnimationFrame(() => {
			const input = popoverRef.current?.querySelector("input");
			if (input) {
				(input as HTMLInputElement).focus();
				(input as HTMLInputElement).select();
			}
		});

		// Keep flag up for a bit to prevent selectionchange from closing the toolbar
		setTimeout(() => {
			isInteractingRef.current = false;
		}, 300);
	}, [getCurrentLinkUrl, saveSelection, calcFloatingPos]);

	const insertLink = useCallback(() => {
		if (!linkUrl.trim()) {
			setShowLinkPopover(false);
			setLinkUrl("");
			return;
		}
		restoreSelection();
		// Remove existing link first if any, then create new
		document.execCommand("unlink", false);
		document.execCommand("createLink", false, linkUrl.trim());
		editorRef.current?.focus();
		setLinkUrl("");
		setShowLinkPopover(false);
		isInteractingRef.current = false;
		checkActiveFormats();
		emitChange();
	}, [linkUrl, restoreSelection, checkActiveFormats, emitChange]);

	const removeLink = useCallback(() => {
		isInteractingRef.current = true;
		restoreSelection();
		document.execCommand("unlink", false);
		editorRef.current?.focus();
		setShowLinkPopover(false);
		setLinkUrl("");
		setTimeout(() => {
			isInteractingRef.current = false;
			checkActiveFormats();
			emitChange();
		}, 50);
	}, [restoreSelection, checkActiveFormats, emitChange]);

	const handleToolbarButtonClick = useCallback(
		(action: () => void) => {
			isInteractingRef.current = true;
			saveSelection();
			action();
			setTimeout(() => {
				isInteractingRef.current = false;
			}, 200);
		},
		[saveSelection]
	);

	// ─── Inserción de imágenes ───
	const insertImage = useCallback(
		(src: string, caption: string) => {
			if (!editorRef.current) return;
			const safeSrc = src.trim().replace(/"/g, "%22");
			const safeCaption = caption.trim() ? escapeHtml(caption.trim()) : "";
			const captionHtml = safeCaption
				? `<figcaption>${safeCaption}</figcaption>`
				: "";
			const figureHtml = `<figure class="editor-figure"><img src="${safeSrc}" alt="${safeCaption}" loading="lazy" />${captionHtml}</figure><p><br></p>`;

			editorRef.current.focus();
			restoreSelection();

			// Asegurar que el cursor esté dentro del editor; si no, colocar al final
			const sel = window.getSelection();
			const inside =
				sel &&
				sel.rangeCount > 0 &&
				editorRef.current.contains(sel.anchorNode);
			if (!inside) {
				const range = document.createRange();
				range.selectNodeContents(editorRef.current);
				range.collapse(false);
				sel?.removeAllRanges();
				sel?.addRange(range);
			}

			document.execCommand("insertHTML", false, figureHtml);
			emitChange();
			checkActiveFormats();
		},
		[restoreSelection, emitChange, checkActiveFormats],
	);

	const pickImageFile = useCallback(() => {
		setImageError("");
		saveSelection();
		imageInputRef.current?.click();
	}, [saveSelection]);

	const handleImageFileChange = useCallback(
		async (e: React.ChangeEvent<HTMLInputElement>) => {
			const file = e.target.files?.[0];
			e.target.value = ""; // permitir volver a elegir el mismo archivo
			if (!file) return;
			if (!file.type.startsWith("image/")) {
				setImageError("Solo se permiten archivos de imagen (JPG, PNG, WebP, GIF).");
				return;
			}
			if (file.size > 15 * 1024 * 1024) {
				setImageError("La imagen es muy grande. Máximo 15 MB.");
				return;
			}
			setImageError("");
			setUploadingImage(true);
			try {
				const url = await uploadImageFile(file, "articles");
				insertImage(url, "");
			} catch (err) {
				const msg = err instanceof Error ? err.message : "Error al subir la imagen.";
				setImageError(classifyUploadError(msg));
			} finally {
				setUploadingImage(false);
			}
		},
		[insertImage],
	);

	const openImagePopover = useCallback(() => {
		isInteractingRef.current = true;
		setImageError("");
		saveSelection();
		setImageUrl("");
		setImageCaption("");
		setShowImagePopover(true);
		setShowLinkPopover(false);
		if (savedRangeRef.current && editorRef.current) {
			const pos = calcFloatingPos(savedRangeRef.current);
			setFloatingPos(pos);
		} else {
			setFloatingPos({ top: 8, left: 8 });
		}
		setTimeout(() => {
			isInteractingRef.current = false;
		}, 300);
	}, [saveSelection, calcFloatingPos]);

	const insertImageFromUrl = useCallback(() => {
		const url = imageUrl.trim();
		if (!url) {
			setShowImagePopover(false);
			return;
		}
		const valid =
			url.startsWith("http://") ||
			url.startsWith("https://") ||
			url.startsWith("data:image/");
		if (!valid) {
			setImageError("La URL debe empezar con http://, https:// o data:image/.");
			return;
		}
		insertImage(url, imageCaption);
		setShowImagePopover(false);
		setImageUrl("");
		setImageCaption("");
		setImageError("");
		isInteractingRef.current = false;
	}, [imageUrl, imageCaption, insertImage]);

	const toggleHeading = (level: string) => {
		exec("formatBlock", level);
	};

	const formatButtons = [
		{ key: "bold", icon: "ri-bold", action: () => exec("bold"), title: "Negrita (Ctrl+B)" },
		{ key: "italic", icon: "ri-italic", action: () => exec("italic"), title: "Itálica (Ctrl+I)" },
		{ key: "underline", icon: "ri-underline", action: () => exec("underline"), title: "Subrayado (Ctrl+U)" },
		{ key: "strike", icon: "ri-strikethrough", action: () => exec("strikeThrough"), title: "Tachado" },
		{ key: "sep1", icon: "", action: () => {}, title: "" },
		{ key: "h2", icon: "ri-h-1", action: () => toggleHeading("h2"), title: "Título" },
		{ key: "h3", icon: "ri-h-2", action: () => toggleHeading("h3"), title: "Subtítulo" },
		{ key: "quote", icon: "ri-double-quotes-l", action: () => exec("formatBlock", "blockquote"), title: "Cita" },
		{ key: "sep2", icon: "", action: () => {}, title: "" },
		{ key: "ul", icon: "ri-list-unordered", action: () => exec("insertUnorderedList"), title: "Lista" },
		{ key: "ol", icon: "ri-list-ordered", action: () => exec("insertOrderedList"), title: "Lista numerada" },
		{ key: "sep3", icon: "", action: () => {}, title: "" },
		{ key: "link", icon: "ri-link", action: openLinkPopover, title: "Enlace (Ctrl+K)" },
		{ key: "unlink", icon: "ri-link-unlink-m", action: removeLink, title: "Quitar enlace" },
	];

	return (
		<div className="relative">
			{label && (
				<label className="block text-[#0B1F3A] font-semibold text-xs mb-2 uppercase tracking-wide">
					{label}
					{required && <span className="text-red-500 ml-0.5">*</span>}
				</label>
			)}

			<div
				className={`rounded-xl border bg-white transition-all overflow-hidden relative ${
					focused ? "border-[#0B1F3A] ring-2 ring-[#0B1F3A]/10" : "border-slate-200"
				}`}
			>
				{/* Fixed top toolbar (minimal) */}
				<div className="px-3 py-2 border-b border-slate-100 bg-slate-50/80 flex items-center gap-1 flex-wrap">
					<button
						type="button"
						onClick={() => exec("undo")}
						title="Deshacer"
						className="w-7 h-7 flex items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 transition-colors cursor-pointer"
					>
						<i className="ri-arrow-go-back-line text-xs" />
					</button>
					<button
						type="button"
						onClick={() => exec("redo")}
						title="Rehacer"
						className="w-7 h-7 flex items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 transition-colors cursor-pointer"
					>
						<i className="ri-arrow-go-forward-line text-xs" />
					</button>
					<div className="w-px h-4 bg-slate-200 mx-1" />
					{formatButtons.map((btn) =>
						btn.key.startsWith("sep") ? (
							<div key={btn.key} className="w-px h-4 bg-slate-200 mx-1" />
						) : (
							<button
								key={btn.key}
								type="button"
								onMouseDown={(e) => {
									e.preventDefault();
									saveSelection();
									btn.action();
								}}
								title={btn.title}
								className={`w-7 h-7 flex items-center justify-center rounded-md text-sm transition-colors cursor-pointer ${
									activeFormats.has(btn.key)
										? "bg-[#0B1F3A] text-white"
										: "text-slate-500 hover:bg-slate-100"
								}`}
							>
								<i className={btn.icon} />
							</button>
						),
					)}
					<div className="w-px h-4 bg-slate-200 mx-1" />
					<button
						type="button"
						onMouseDown={(e) => {
							e.preventDefault();
							saveSelection();
						}}
						onClick={pickImageFile}
						disabled={uploadingImage}
						title="Subir imagen"
						className="h-7 px-2 flex items-center gap-1 rounded-md text-slate-500 hover:bg-slate-100 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-wait"
					>
						{uploadingImage ? (
							<i className="ri-loader-4-line animate-spin text-xs" />
						) : (
							<i className="ri-image-add-line text-xs" />
						)}
						<span className="text-[10px] font-semibold">
							{uploadingImage ? "Subiendo…" : "Imagen"}
						</span>
					</button>
					<button
						type="button"
						onMouseDown={(e) => {
							e.preventDefault();
							saveSelection();
						}}
						onClick={openImagePopover}
						title="Insertar imagen por URL"
						className="w-7 h-7 flex items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 transition-colors cursor-pointer"
					>
						<i className="ri-links-line text-xs" />
					</button>
					<input
						ref={imageInputRef}
						type="file"
						accept="image/jpeg,image/png,image/webp,image/gif"
						onChange={handleImageFileChange}
						className="hidden"
					/>
				</div>
				{imageError && (
					<div className="px-3 py-2 bg-red-50 border-b border-red-100 flex items-start gap-2">
						<i className="ri-error-warning-line text-red-500 text-xs mt-0.5 flex-shrink-0" />
						<p className="text-red-600 text-[11px] leading-relaxed">{imageError}</p>
					</div>
				)}

				{/* Editable area */}
				<div className="relative">
					<div
						ref={editorRef}
						contentEditable
						suppressContentEditableWarning
						onInput={handleInput}
						onFocus={() => {
							setFocused(true);
							checkActiveFormats();
						}}
						onBlur={() => {
							setFocused(false);
							emitChange();
						}}
						onMouseUp={handleMouseUp}
						onTouchEnd={handleMouseUp}
						onKeyDown={handleKeyDown}
						onKeyUp={checkActiveFormats}
						onPaste={handlePaste}
						data-placeholder={placeholder}
						className="px-4 py-3 text-sm text-slate-700 leading-relaxed outline-none rich-editor empty:before:content-[attr(data-placeholder)] empty:before:text-slate-400 empty:before:pointer-events-none"
						style={{ minHeight }}
					/>

					{/* Floating toolbar */}
					{showFloating && !showLinkPopover && (
						<div
							ref={toolbarRef}
							className="absolute z-30 bg-[#0B1F3A] rounded-xl shadow-2xl px-2 py-1.5 flex items-center gap-0.5 animate-in fade-in zoom-in-95 duration-150"
							style={{
								top: Math.max(4, floatingPos.top),
								left: Math.max(4, floatingPos.left),
								transform: "translateY(-100%)",
							}}
						>
							{/* Arrow */}
							<div className="absolute left-1/2 -translate-x-1/2 -bottom-1.5 w-3 h-3 bg-[#0B1F3A] rotate-45" />
							{formatButtons.filter((b) => !b.key.startsWith("sep")).map((btn) => (
								<button
									key={btn.key}
									type="button"
									onMouseDown={(e) => {
										e.preventDefault();
										e.stopPropagation();
										// Prevent default selection loss, then run action with guard
										isInteractingRef.current = true;
										saveSelection();
										btn.action();
										setTimeout(() => {
											isInteractingRef.current = false;
										}, 250);
									}}
									title={btn.title}
									className={`relative w-8 h-8 flex items-center justify-center rounded-lg text-sm transition-all cursor-pointer ${
										activeFormats.has(btn.key)
											? "bg-white/20 text-white"
											: "text-white/70 hover:bg-white/10 hover:text-white"
										}`}
								>
									<i className={btn.icon} />
								</button>
							))}
						</div>
					)}

					{/* Link popover */}
					{showLinkPopover && (
						<div
							ref={popoverRef}
							className="absolute z-40 bg-white border border-slate-200 rounded-xl shadow-2xl p-3 flex items-center gap-2 animate-in fade-in slide-in-from-top-2 duration-150"
							style={{
								top: Math.max(4, floatingPos.top + 8),
								left: Math.max(4, floatingPos.left),
								width: 340,
							}}
							onMouseDown={(e) => e.stopPropagation()}
						>
							<i className="ri-link text-slate-400 text-xs" />
							<input
								ref={(el) => {
									if (el && showLinkPopover) {
										setTimeout(() => el.focus(), 0);
									}
								}}
								type="url"
								value={linkUrl}
								onChange={(e) => setLinkUrl(e.target.value)}
								placeholder="https://ejemplo.com"
								className="flex-1 px-2.5 py-1.5 rounded-md border border-slate-200 text-xs focus:outline-none focus:border-[#0B1F3A] focus:ring-1 focus:ring-[#0B1F3A]/20"
								onKeyDown={(e) => {
									if (e.key === "Enter") {
										e.preventDefault();
										insertLink();
									}
									if (e.key === "Escape") {
										setShowLinkPopover(false);
										setLinkUrl("");
										isInteractingRef.current = false;
									}
								}}
							/>
							<button
								type="button"
								onClick={insertLink}
								className="px-3 py-1.5 bg-[#0B1F3A] text-white text-xs font-semibold rounded-md hover:bg-slate-800 transition-colors cursor-pointer whitespace-nowrap"
							>
								Aplicar
							</button>
							<button
								type="button"
								onClick={() => {
									setShowLinkPopover(false);
									setLinkUrl("");
									isInteractingRef.current = false;
								}}
								className="px-2 py-1.5 text-slate-400 hover:text-slate-600 text-xs cursor-pointer whitespace-nowrap"
							>
								Cancelar
							</button>
						</div>
					)}
					{/* Image by URL popover */}
					{showImagePopover && (
						<div
							ref={popoverRef}
							className="absolute z-40 bg-white border border-slate-200 rounded-xl shadow-2xl p-3 flex flex-col gap-2 animate-in fade-in slide-in-from-top-2 duration-150"
							style={{
								top: Math.max(4, floatingPos.top + 8),
								left: Math.max(4, floatingPos.left),
								width: 340,
							}}
							onMouseDown={(e) => e.stopPropagation()}
						>
							<div className="flex items-center gap-2">
								<i className="ri-image-line text-slate-400 text-xs" />
								<input
									autoFocus
									type="url"
									value={imageUrl}
									onChange={(e) => setImageUrl(e.target.value)}
									placeholder="https://ejemplo.com/imagen.jpg"
									className="flex-1 px-2.5 py-1.5 rounded-md border border-slate-200 text-xs focus:outline-none focus:border-[#0B1F3A] focus:ring-1 focus:ring-[#0B1F3A]/20"
									onKeyDown={(e) => {
										if (e.key === "Enter") {
											e.preventDefault();
											insertImageFromUrl();
										}
										if (e.key === "Escape") {
											setShowImagePopover(false);
											isInteractingRef.current = false;
										}
									}}
								/>
							</div>
							<input
								type="text"
								value={imageCaption}
								onChange={(e) => setImageCaption(e.target.value)}
								placeholder="Pie de foto (opcional)"
								className="w-full px-2.5 py-1.5 rounded-md border border-slate-200 text-xs focus:outline-none focus:border-[#0B1F3A] focus:ring-1 focus:ring-[#0B1F3A]/20"
								onKeyDown={(e) => {
									if (e.key === "Enter") {
										e.preventDefault();
										insertImageFromUrl();
									}
								}}
							/>
							<div className="flex items-center justify-end gap-2">
								<button
									type="button"
									onClick={() => {
										setShowImagePopover(false);
										isInteractingRef.current = false;
									}}
									className="px-2 py-1.5 text-slate-400 hover:text-slate-600 text-xs cursor-pointer whitespace-nowrap"
								>
									Cancelar
								</button>
								<button
									type="button"
									onClick={insertImageFromUrl}
									className="px-3 py-1.5 bg-[#0B1F3A] text-white text-xs font-semibold rounded-md hover:bg-slate-800 transition-colors cursor-pointer whitespace-nowrap"
								>
									Insertar
								</button>
							</div>
						</div>
					)}
				</div>
			</div>

			<p className="text-slate-400 text-[10px] mt-1.5">
				{allowHtmlPaste
					? "Puedes pegar texto con formato desde Word u otras fuentes. El sistema limpiará estilos extraños pero mantendrá negritas, citas y listas. También puedes usar Ctrl+B, Ctrl+I, Ctrl+U."
					: "Selecciona cualquier texto y aparecerá el toolbar de formato. También puedes usar Ctrl+B, Ctrl+I, Ctrl+U."}
			</p>
		</div>
	);
}